/**
 * ErrorBanner - エラー表示用バナーコンポーネント
 * Phase 2: 再利用可能なエラーバナーUI
 */

import React from 'react';
import { AlertCircle, RefreshCw, X, Wifi, Server, AlertTriangle, Info } from 'lucide-react';
import './ErrorBanner.css';

interface ErrorState {
  type: 'none' | 'roomId' | 'network' | 'server' | 'partial' | 'unknown';
  message: string;
  details?: string;
  retryable: boolean;
}

interface ErrorBannerProps {
  error: ErrorState;
  onRetry?: () => void;
  onDismiss: () => void;
  loading?: boolean;
}

/**
 * エラーバナーコンポーネント
 * エラー種別に応じた適切なアイコンとスタイリングを提供
 */
export const ErrorBanner: React.FC<ErrorBannerProps> = ({ 
  error, 
  onRetry, 
  onDismiss, 
  loading = false 
}) => {
  if (error.type === 'none') return null;

  // エラー種別に応じたアイコンを取得
  const getErrorIcon = () => {
    switch (error.type) {
      case 'roomId':
        return <AlertTriangle size={20} />;
      case 'network':
        return <Wifi size={20} />;
      case 'server':
        return <Server size={20} />;
      case 'partial':
        return <Info size={20} />;
      case 'unknown':
        return <AlertCircle size={20} />;
      default:
        return <AlertCircle size={20} />;
    }
  };

  // エラー種別に応じたアクセシビリティ用のロール
  const getAriaRole = () => {
    switch (error.type) {
      case 'roomId':
      case 'server':
        return 'alert';
      case 'network':
      case 'partial':
        return 'status';
      default:
        return 'alert';
    }
  };

  return (
    <div 
      className={`error-banner ${error.type}`}
      role={getAriaRole()}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="error-content">
        <div className="error-icon" aria-hidden="true">
          {getErrorIcon()}
        </div>
        <div className="error-text">
          <h4 className="error-message">{error.message}</h4>
          {error.details && (
            <p className="error-details">{error.details}</p>
          )}
        </div>
      </div>
      
      <div className="error-actions">
        {error.retryable && onRetry && (
          <button 
            onClick={onRetry} 
            className="retry-button"
            disabled={loading}
            aria-label="再試行"
          >
            <RefreshCw 
              size={16} 
              className={loading ? 'spinning' : ''} 
            />
            {loading ? '処理中...' : '再試行'}
          </button>
        )}
        <button 
          onClick={onDismiss} 
          className="dismiss-button"
          aria-label="エラーを閉じる"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

/**
 * トースト通知形式のエラーバナー
 * 一定時間後に自動で消える軽量版
 */
interface ErrorToastProps {
  error: ErrorState;
  autoHideDuration?: number;
  onHide?: () => void;
}

export const ErrorToast: React.FC<ErrorToastProps> = ({
  error,
  autoHideDuration = 5000,
  onHide
}) => {
  React.useEffect(() => {
    if (error.type !== 'none' && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        if (onHide) {
          onHide();
        }
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [error.type, autoHideDuration, onHide]);

  if (error.type === 'none') return null;

  return (
    <div className={`error-toast ${error.type}`}>
      <div className="error-content">
        <div className="error-icon">
          <AlertCircle size={18} />
        </div>
        <div className="error-text">
          <span className="error-message">{error.message}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * エラー統計情報コンポーネント
 * 部分失敗時の詳細情報表示用
 */
interface ErrorStatsProps {
  totalCount: number;
  failedCount: number;
  successCount: number;
  onRetryFailed?: () => void;
}

export const ErrorStats: React.FC<ErrorStatsProps> = ({
  totalCount,
  failedCount,
  successCount,
  onRetryFailed
}) => {
  if (failedCount === 0) return null;

  const successRate = totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0;

  return (
    <div className="error-stats">
      <div className="stats-content">
        <div className="stats-numbers">
          <span className="success-count">{successCount}枚成功</span>
          <span className="failed-count">{failedCount}枚失敗</span>
          <span className="success-rate">成功率: {successRate}%</span>
        </div>
        {onRetryFailed && (
          <button 
            onClick={onRetryFailed}
            className="retry-failed-button"
          >
            失敗分を再試行
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorBanner;